import {
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  resource,
  signal,
  viewChild
} from '@angular/core';
import mermaid from 'mermaid';
import {AppFormat} from '../../lib/format';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {Alerts, DialogButton} from '../../lib/alerts';
import {FormsModule} from '@angular/forms';
import {countBy} from 'es-toolkit';
import {CollectionView} from '@mescius/wijmo';
import {WjChartModule} from '@mescius/wijmo.angular2.chart';
import {Wijmo} from '../../lib/wjgrid-utils';
import {AccountDataStatusEnum, SegmentApi, SegmentStatusEnum, SegmentStatusLog} from '../../../api-client-kt/src';
import {WjCharts} from '../../lib/wjcharts';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {AccessControlService} from '../../security/access-control.service';
import {AccountSegmentStatisticsCalculator} from '../../data/portfolio-segment-statistics';
import {FlexChart, FlexPie} from '@mescius/wijmo.chart';
import {AccountGridComponent, AccountGridFilterCondition} from '../account-grid/account-grid.component';
import {ActivatedRoute, Router} from '@angular/router';

const diagramSrc = `
        %%{init: {
          "theme": "base",
          "themeVariables": {
            "clusterBkg": "#ffffff",
            "clusterBorder": "#ff0000"
          }
        }}%%

        flowchart LR

        %% --- Tiering workflow ---
        direction LR
        T1[Open]
        T2[Local Review]
        T3[Global Review]
        T4[Approved]

        T1 --> T2 --> T3 --> T4

        %% --- Styling to resemble the red buttons ---
        classDef nrmNode fill:#e2e2e2,color:#000000,stroke:#900000,stroke-width:2px,rx:6,ry:6;
        classDef redNode fill:#C21B17FF,color:#ffffff,stroke:#900000,stroke-width:2px,rx:6,ry:6;

        class T1,T2,T3,T4 nrmNode;
    `;

@Component({
    selector: 'app-segment-status',
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
        FormsModule,
        WjChartModule
    ],
    templateUrl: './segment-status.component.html',
    styleUrl: './segment-status.component.scss',
})
export class SegmentStatusComponent {

    protected readonly AppFormat = AppFormat;

    protected statusChangeAllowed = signal(false);
    protected statusChangeComment: string | undefined = undefined;

    protected readonly pfData = inject(PortfolioDataContext);
    protected readonly pfSegData = inject(PortfolioSegmentDataContext);

    protected readonly totalAccounts = signal<number>(0);
    protected readonly chartData = new CollectionView( [
        { status: AccountDataStatusEnum.Open, count: 0 },
        { status: AccountDataStatusEnum.Completed, count: 0 },
        { status: AccountDataStatusEnum.NeedsReview, count: 0 },
    ]);

    protected readonly Wijmo = Wijmo;
    protected readonly GeneraliPieChartPalette = WjCharts.GeneraliPieChartPalette;
    protected readonly SegmentStatusEnum = SegmentStatusEnum;

    private mermaidEl = viewChild.required<ElementRef<HTMLElement>>('mermaid');

    protected readonly portfolioName = computed(() => this.pfSegData.activeSegment.value()?.segmentName);

    private readonly segmentApi = inject(SegmentApi);
    private readonly accessControlSvc = inject(AccessControlService);

    protected readonly segmentStatusHistory = resource({
        params: () => this.pfSegData.segmentId(),
        loader: ({ params }) => this.loadStatusLogs(params),
        defaultValue: []
    });

    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);

    constructor() {
        mermaid.initialize({ startOnLoad: false, securityLevel: 'loose' });
        effect(async () => this.refreshStatisticsWithPortfolioData());
        effect(async () => await this.renderMermaidDiagram());
        effect(() => this.updateStatusChangedAllowed());
    }

    async ngAfterViewInit(): Promise<void> {
        await this.segmentApi.getStatusLogs({ id: this.pfSegData.segmentId()! });
    }

    private updateStatusChangedAllowed() {
        const segmentStatusChangePermission = this.accessControlSvc.canEditSegmentStatus(this.pfSegData.activeSegment.value(), this.pfSegData.portfolio.value());
        this.statusChangeAllowed.set(segmentStatusChangePermission.allowed);
    }

    private async loadStatusLogs(segmentId: string | undefined): Promise<SegmentStatusLog[]> {
        if (!segmentId) return [];
        else return this.segmentApi.getStatusLogs({ id: segmentId });
    }

    private async renderMermaidDiagram() {
        let src: string;
        switch (this.pfSegData.activeSegment.value()?.status) {
            case SegmentStatusEnum.Open: src = diagramSrc + 'class T1 redNode;'; break;
            case SegmentStatusEnum.InLocalReview: src = diagramSrc + 'class T2 redNode;'; break;
            case SegmentStatusEnum.InGlobalReview: src = diagramSrc + 'class T3 redNode;'; break;
            case SegmentStatusEnum.Approved: src = diagramSrc + 'class T4 redNode;'; break;
            default: src = diagramSrc;
        }
        const svg = await mermaid.render('someid', src);
        this.mermaidEl().nativeElement.innerHTML = svg.svg;
    }

    protected async setStatus(status: SegmentStatusEnum) {
        if (!this.statusChangeComment) {
            await Alerts.alertPopup({
                error: true,
                body: 'Please enter a comment before changing the status.',
            });
            return;
        } else {
            const statusChange = `"${this.pfSegData.segmentStatus()}" to "${status}"`;
            await Alerts.alertPopup({
                body: `You are changing the status from ${statusChange}. Are you sure?`,
                buttons: [DialogButton.Cancel, DialogButton.Ok ]
            });
            await this.pfSegData.updateSegmentStatus(status, this.statusChangeComment);
            this.segmentStatusHistory.reload();
            // update comment in next cycle, otherwise Angular complains about the change detection cycle
            setTimeout(() => this.statusChangeComment = undefined);
        }
    }

    private refreshStatisticsWithPortfolioData() {
        const period = this.pfSegData.periodFilter();
        const uwYear = this.pfSegData.portfolio.value()?.uwyear;
        const accounts = this.pfSegData.getAccountData()
            .filter(AccountSegmentStatisticsCalculator.accountPeriodFilter(uwYear, period));

        const counts = countBy(accounts, a => a.status ?? AccountDataStatusEnum.Open);

        Object.keys(counts).forEach(key => {
            this.chartData.items.find(it => it.status === key)!.count = counts[key as keyof typeof counts] ?? 0;
        });
        this.totalAccounts.set(accounts.length);
        this.chartData.refresh();
    }

    protected async onChartClick(chart: FlexChart | FlexPie, event: MouseEvent) {
        const ht = chart.hitTest(event);
        const item = ht.item;

        if (item && ht.distance === 0) {
            const filter: AccountGridFilterCondition<string> = { column: 'status', type: 'equals', value1: item.status };
            await AccountGridComponent.applyFilterAndShow(this.router, this.route, filter);
        }
    }
}
